import { useEffect, useMemo, useRef, useState } from 'react'
import { NowMarker } from './lib.js'
import {
  couchesEmpilees, formatValue, indexDuBucket, jourSurLAxe, libelleDuBucket, niceScale, nowFraction, titreDuBucket,
  type ChartValueFormat,
} from './chart-scale.js'

export { formatValue, niceScale }
export type { ChartValueFormat }

/**
 * CHART LIVE CONTROL — le graphique générique du design system (SVG maison).
 *
 * Types : courbe · aire · barres · barres groupées · barres empilées ·
 * cumul (courbe montée sur données cumulées). Multi-séries avec palette
 * sémantique. Transition douce au changement de données (crossfade +
 * morph d'opacité) — désactivée en reduced-motion.
 *
 * Tracé UNIQUEMENT depuis des points réels — pas de données, pas de dessin.
 *
 * ÉCHELLE DE VALEURS (28/08/2026). Le graphique n'avait qu'un axe temporel :
 * on voyait une courbe monter sans savoir de combien. L'axe des valeurs est
 * en HTML, pas en SVG — le tracé est étiré (`preserveAspectRatio="none"`)
 * et du texte SVG y serait déformé horizontalement. C'est aussi pour cela
 * que les libellés de temps vivaient déjà hors du SVG.
 *
 * LECTURE PRÉCISE AU SURVOL. Un `<title>` natif donnait un rectangle gris
 * système, après une seconde d'attente, sans la référence ni l'écart. Le
 * survol pilote désormais un repère vertical, les points de chaque série et
 * un panneau qui reste toujours dans le cadre.
 */

export type ChartKind = 'line' | 'area' | 'bars' | 'grouped' | 'stacked' | 'cumulative' | 'stacked_cumulative'

export interface ChartSeries {
  key: string
  label: string
  points: { t: string; value: number }[]
  /** Couleur imposée (une billetterie garde la sienne, 19/10/2026) ; sinon la palette, dans l'ordre. */
  color?: string
  /**
   * Tracée PAR-DESSUS, jamais empilée : la référence N-1 sur un graphique
   * empilé. Empilée, elle s'ajoutait au total de la nuit.
   */
  overlay?: boolean
  /** Jamais tracée : sert seulement à l'écart du panneau (« Shotgun contre Shotgun »). */
  hidden?: boolean
}

/** Un repère horizontal (« Entrées BDE attendues ») — une valeur absolue, jamais re-cumulée. */
export interface ChartGuide { value: number; label: string; color?: string }

/** Bande de prévision : une FOURCHETTE (zone basse → haute), jamais une
 *  fausse valeur précise. Valeurs ABSOLUES (pas re-cumulées). */
export interface ChartBand {
  label: string
  points: { t: string; low: number; high: number }[]
  color?: string
}

const PALETTE = ['var(--violet)', 'var(--cyan)', 'var(--emerald)', 'var(--amber)', 'var(--rose)', 'var(--blue, #4f7cff)']

const W = 720
const H = 236
const PAD_X = 6
const PAD_TOP = 14
const PAD_BOTTOM = 26

export function seriesColor(index: number): string { return PALETTE[index % PALETTE.length]! }

const CUMULES: ReadonlySet<ChartKind> = new Set(['cumulative', 'stacked_cumulative'])
const COURBES: ReadonlySet<ChartKind> = new Set(['line', 'area', 'cumulative', 'stacked_cumulative'])
const EMPILES: ReadonlySet<ChartKind> = new Set(['stacked', 'stacked_cumulative'])

interface Model {
  kind: ChartKind
  buckets: string[]
  x: (i: number) => number
  y: (v: number) => number
  tX: (tMs: number) => number
  /** Bornes RÉELLES de l'axe : début du premier bucket, fin du dernier. */
  t0: number
  tEnd: number
  /** Étendue horizontale RÉELLEMENT tracée. Nulle quand l'axe n'a qu'un
   *  bucket en mode courbe : il n'y a alors pas d'axe où situer un instant. */
  spanMs: number
  max: number
  bw: number
  labels: { x: number; text: string }[]
  ticks: number[]
  data: { key: string; label: string; color: string; values: number[]; overlay: boolean; hidden: boolean }[]
  /** Les couches empilées (cumul empilé, barres empilées) : bas et haut de chaque série tracée. */
  couches: Map<string, { bas: number[]; haut: number[] }>
  /** Le jour figure sur les heures (ventes sur plusieurs jours). */
  avecJour: boolean
}

function buildModel(kind: ChartKind, series: ChartSeries[], bucketS: number, band?: ChartBand, guides?: readonly ChartGuide[]): Model | null {
  const visible = series.filter((s) => s.points.length > 0)
  if (visible.length === 0 && (!band || band.points.length === 0)) return null
  const allT = [...new Set([
    ...visible.filter((s) => !s.hidden).flatMap((s) => s.points.map((p) => p.t)),
    ...(band?.points.map((p) => p.t) ?? []),
  ])].sort()
  if (allT.length === 0) return null
  const stepMs = bucketS * 1000
  const t0 = new Date(allT[0]!).getTime()
  const nBuckets = Math.max(1, indexDuBucket(new Date(allT[allT.length - 1]!).getTime(), t0, bucketS) + 1)
  const tEnd = t0 + nBuckets * stepMs
  /* Un jour se lit à midi : le début d'un jour de 25 h + i × 24 h retomberait
     la veille au soir, et l'étiquette dirait la mauvaise date. */
  const buckets = Array.from({ length: nBuckets }, (_, i) => new Date(t0 + i * stepMs + (bucketS >= 86400 ? 12 * 3_600_000 : 0)).toISOString())

  const empile = EMPILES.has(kind)
  const data = visible.map((s) => {
    const values = new Array<number>(nBuckets).fill(0)
    for (const p of s.points) {
      const idx = indexDuBucket(new Date(p.t).getTime(), t0, bucketS)
      if (idx >= 0 && idx < nBuckets) values[idx] = (values[idx] ?? 0) + p.value
    }
    if (CUMULES.has(kind)) {
      let acc = 0
      for (let k = 0; k < values.length; k++) { acc += values[k]!; values[k] = acc }
    }
    return {
      key: s.key, label: s.label, color: s.color ?? seriesColor(series.indexOf(s)), values,
      overlay: !!s.overlay, hidden: !!s.hidden,
    }
  })

  // L'empilement ne prend que les couches : la référence passe par-dessus.
  const enCouches = data.filter((d) => !d.overlay && !d.hidden)
  const couches = new Map<string, { bas: number[]; haut: number[] }>()
  if (empile) {
    // Les valeurs sont déjà cumulées pour le cumul empilé : on n'empile qu'ici.
    couchesEmpilees(enCouches.map((d) => d.values), false).forEach((c, i) => couches.set(enCouches[i]!.key, c))
  }

  const brut = Math.max(
    empile
      ? Math.max(1, ...buckets.map((_, i) => enCouches.reduce((a, d) => a + d.values[i]!, 0)))
      : Math.max(1, ...data.filter((d) => !d.hidden).flatMap((d) => d.values)),
    ...data.filter((d) => d.overlay).flatMap((d) => d.values),
    ...(band?.points.map((p) => p.high) ?? [0]),
    ...(guides?.map((g) => g.value) ?? [0]),
  )
  /*
   * LIGNE DE BASE À ZÉRO. Cadrer sur 81–94 aurait fait ressembler une
   * progression de treize billets à une explosion. Sur des comptes cumulés
   * positifs, l'origine fait partie de l'information.
   */
  const { max, ticks } = niceScale(brut)

  const innerW = W - PAD_X * 2
  const courbe = COURBES.has(kind)
  const x = (i: number) => PAD_X + (i / Math.max(nBuckets - (courbe ? 1 : 0), 1)) * innerW
  const y = (v: number) => PAD_TOP + (1 - v / max) * (H - PAD_TOP - PAD_BOTTOM)
  const tX = (tMs: number) => PAD_X + Math.max(0, Math.min(1, (tMs - t0) / Math.max(tEnd - stepMs - t0, 1))) * innerW
  const bw = innerW / nBuckets
  /* Une courbe pose ses points AU début de chaque bucket : son étendue
     s'arrête donc au début du DERNIER. Une barre occupe le bucket entier.
     Les deux ne situent pas le même instant au même endroit. */

  const avecJour = jourSurLAxe(t0, tEnd, bucketS)
  const every = Math.max(1, Math.ceil(nBuckets / (avecJour ? 5 : 6)))
  const labels = buckets
    .map((t, i) => ({ i, t }))
    .filter(({ i }) => i % every === 0)
    .map(({ i, t }) => ({ x: courbe ? x(i) : PAD_X + i * bw + bw / 2, text: libelleDuBucket(new Date(t).getTime(), bucketS, avecJour) }))

  return { kind, buckets, x, y, tX, t0, tEnd, spanMs: courbe ? tEnd - stepMs - t0 : tEnd - t0, max, bw, labels, ticks, data, couches, avecJour }
}

export function Chart({ kind, series, bucketS, ariaLabel, band, valueFormat = 'integer', nowMs = null, guides, ecartContre, ecartLibelle, sansEcart = false }: {
  kind: ChartKind
  series: ChartSeries[]
  bucketS: number
  ariaLabel: string
  band?: ChartBand | null
  valueFormat?: ChartValueFormat
  /** Repères horizontaux (« Entrées BDE attendues »). */
  guides?: readonly ChartGuide[] | undefined
  /**
   * La série à laquelle la référence (`__ref`) se compare dans le panneau.
   * Absente : le TOTAL des séries tracées — toute la nuit, défaut de
   * l'exploitant (19/10/2026) ; une seule série, c'est elle.
   */
  ecartContre?: string | undefined
  /** « vs N-1 · toute la nuit » — ce que dit l'écart. */
  ecartLibelle?: string | undefined
  /**
   * Une série est masquée dans la légende : le total n'est plus celui de la
   * nuit, l'écart n'est pas donné (il comparerait une part au N-1 entier).
   */
  sansEcart?: boolean
  /**
   * Instant à matérialiser sur l'axe. `null` par défaut : le graphique ne
   * décide JAMAIS tout seul qu'il faut afficher Maintenant — c'est l'appelant
   * qui sait si sa soirée est en cours. Aucun `Date.now()` ici.
   */
  nowMs?: number | null
}) {
  const model = useMemo(() => buildModel(kind, series, bucketS, band ?? undefined, guides), [kind, series, bucketS, band, guides])
  // Transition douce au changement de données : bref crossfade.
  const [fresh, setFresh] = useState(true)
  useEffect(() => {
    setFresh(false)
    const t = setTimeout(() => setFresh(true), 20)
    return () => clearTimeout(t)
  }, [model])

  /** Index de bucket survolé — `null` hors survol. */
  const [hover, setHover] = useState<number | null>(null)
  const zoneRef = useRef<HTMLDivElement>(null)

  if (!model) return <p className="lc-hint so-pad">Aucune donnée à tracer.</p>
  const { x, y, bw, data, labels, ticks, couches } = model
  const traces = data.filter((d) => !d.hidden)
  const courbe = COURBES.has(kind)
  const baseline = y(0)

  /*
   * POINTEUR → BUCKET. Un seul gestionnaire sur la zone de tracé, pas une
   * cible par barre : les courbes n'ont pas de surface cliquable, et
   * multiplier les zones invisibles rendrait le survol nerveux.
   *
   * `pointermove` couvre souris ET tactile : sur mobile, glisser le doigt
   * déplace la lecture au lieu d'exiger un survol impossible.
   */
  const surZone = (clientX: number) => {
    const el = zoneRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const ratio = (clientX - r.left) / Math.max(r.width, 1)
    const n = model.buckets.length
    const i = Math.round(ratio * (n - 1))
    setHover(Math.max(0, Math.min(n - 1, i)))
  }

  const iHover = hover
  const bucketT = iHover === null ? null : model.buckets[iHover]
  const centreX = iHover === null ? 0
    : courbe ? x(iHover) : PAD_X + iHover * bw + bw / 2
  const pct = (centreX / W) * 100
  /* Le panneau bascule du côté opposé au pointeur quand celui-ci approche du
     bord droit : il reste ainsi toujours entièrement dans le cadre. */
  const aDroite = pct > 55

  const lignes = iHover === null ? [] : traces.map((d) => ({
    key: d.key, label: d.label, color: d.color, value: d.values[iHover]!,
  }))
  const reference = lignes.find((l) => l.key === '__ref') ?? null
  const propres = lignes.filter((l) => l.key !== '__ref')
  /* Plusieurs séries : le panneau donne aussi leur TOTAL — sur un graphique
     empilé, c'est la hauteur de la pile ; ailleurs, la somme qu'on cherchait
     de tête. */
  const total = propres.length > 1 ? propres.reduce((a, l) => a + l.value, 0) : null
  const contre = iHover === null ? null
    : ecartContre
      ? (data.find((d) => d.key === ecartContre)?.values[iHover] ?? null)
      : (total ?? propres[0]?.value ?? null)
  const ecart = !sansEcart && contre !== null && reference
    ? { abs: contre - reference.value,
        pct: reference.value === 0 ? null : ((contre - reference.value) / reference.value) * 100 }
    : null

  return (
    <div className="so-chart" role="img" aria-label={ariaLabel}>
      {/* AXE DES VALEURS — en HTML, aligné sur les mêmes graduations que la
          grille SVG. À gauche : on lit l'échelle avant la courbe. */}
      <div className="so-chart__yaxis" aria-hidden="true">
        {ticks.map((v) => (
          <span key={v} style={{ top: `${(y(v) / H) * 100}%` }}>{formatValue(v, valueFormat, true)}</span>
        ))}
      </div>

      <div
        className="so-chart__plot"
        ref={zoneRef}
        onPointerMove={(e) => surZone(e.clientX)}
        onPointerLeave={() => setHover(null)}
      >
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={fresh ? 'so-chart--in' : 'so-chart--out'}>
          {/* Grille horizontale : présente, jamais bruyante. */}
          <g aria-hidden="true" className="so-chart__grid">
            {ticks.map((v) => (
              <line key={v} x1={PAD_X} x2={W - PAD_X} y1={y(v)} y2={y(v)} />
            ))}
          </g>
          {band && band.points.length > 1 ? (() => {
            // Bande de prévision : zone basse → haute + bordures discrètes.
            const pts = [...band.points].sort((a, b) => a.t.localeCompare(b.t))
            const color = band.color ?? 'var(--amber)'
            const lowPath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${model.tX(new Date(p.t).getTime())} ${y(p.low)}`).join(' ')
            const poly = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${model.tX(new Date(p.t).getTime())} ${y(p.high)}`).join(' ')
              + ' ' + [...pts].reverse().map((p) => `L ${model.tX(new Date(p.t).getTime())} ${y(p.low)}`).join(' ') + ' Z'
            return (
              <g aria-hidden="true">
                <path d={poly} fill={color} opacity={0.12} />
                <path d={lowPath} fill="none" stroke={color} strokeWidth={1.2} strokeDasharray="4 4" opacity={0.55} />
                <path d={pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${model.tX(new Date(p.t).getTime())} ${y(p.high)}`).join(' ')}
                  fill="none" stroke={color} strokeWidth={1.2} strokeDasharray="4 4" opacity={0.55} />
              </g>
            )
          })() : null}
          {kind === 'stacked_cumulative' ? (
            /* CUMUL EMPILÉ : une couche par billetterie, le bord haut de la
               dernière est le total de la nuit. */
            traces.filter((d) => !d.overlay).map((d) => {
              const c = couches.get(d.key)
              if (!c) return null
              const haut = c.haut.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ')
              const bas = [...c.bas].map((v, i) => ({ v, i })).reverse().map(({ v, i }) => `L ${x(i)} ${y(v)}`).join(' ')
              const n = c.haut.length - 1
              return (
                <g key={d.key}>
                  <path d={`${haut} ${bas} Z`} fill={d.color} opacity={0.28} />
                  <path d={haut} fill="none" stroke={d.color} strokeWidth={2.2} strokeLinejoin="round" />
                  <circle cx={x(n)} cy={y(c.haut[n]!)} r={3.5} fill={d.color} />
                </g>
              )
            })
          ) : courbe ? (
            traces.map((d) => {
              const line = d.values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(v)}`).join(' ')
              const area = `${line} L ${x(d.values.length - 1)} ${baseline} L ${x(0)} ${baseline} Z`
              return (
                <g key={d.key}>
                  {(kind === 'area' || kind === 'cumulative') ? (
                    <path d={area} fill={d.color} opacity={0.13} />
                  ) : null}
                  <path d={line} fill="none" stroke={d.color} strokeWidth={2.5} strokeLinejoin="round" />
                  <circle cx={x(d.values.length - 1)} cy={y(d.values[d.values.length - 1]!)} r={4} fill={d.color} />
                </g>
              )
            })
          ) : kind === 'stacked' ? (
            model.buckets.map((_, i) => {
              let acc = 0
              return (
                <g key={i}>
                  {traces.filter((d) => !d.overlay).map((d) => {
                    const v = d.values[i]!
                    const y1 = y(acc + v)
                    const hgt = baseline - y(v)
                    acc += v
                    return v > 0
                      ? <rect key={d.key} x={PAD_X + i * bw + 1.5} y={y1} width={Math.max(bw - 3, 2)} height={hgt} fill={d.color} opacity={0.8} rx={2} />
                      : null
                  })}
                </g>
              )
            })
          ) : (
            // bars / grouped : une colonne par bucket, sous-colonnes par série.
            model.buckets.map((_, i) => {
              const barres = traces.filter((d) => !d.overlay || !EMPILES.has(kind))
              const n = barres.length
              const slice = Math.max((bw - 3) / n, 2)
              return (
                <g key={i}>
                  {barres.map((d, k) => {
                    const v = d.values[i]!
                    return v > 0
                      ? <rect key={d.key} x={PAD_X + i * bw + 1.5 + k * slice} y={y(v)} width={Math.max(slice - 1, 1.5)} height={baseline - y(v)} fill={d.color} opacity={0.85} rx={2} />
                      : null
                  })}
                </g>
              )
            })
          )}
          {/* La référence PAR-DESSUS un graphique empilé : une ligne tiretée,
              jamais une couche de plus. Sur des barres empilées, elle relie le
              milieu des barres. */}
          {EMPILES.has(kind) ? traces.filter((d) => d.overlay).map((d) => {
            const px = (i: number) => (courbe ? x(i) : PAD_X + i * bw + bw / 2)
            return (
              <path key={d.key} d={d.values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${px(i)} ${y(v)}`).join(' ')}
                fill="none" stroke={d.color} strokeWidth={2} strokeDasharray="5 4" strokeLinejoin="round" />
            )
          }) : null}
          {/* Repères horizontaux : une valeur attendue, pas une mesure. */}
          {/* Nommé sur le tracé : un pointillé sans mot ne se relie à rien. */}
          {(guides ?? []).map((g, k) => (
            <g key={k}>
              <line x1={PAD_X} x2={W - PAD_X} y1={y(g.value)} y2={y(g.value)}
                stroke={g.color ?? 'var(--text-2)'} strokeWidth={1.4} strokeDasharray="2 5" />
              <text x={PAD_X + 4} y={y(g.value) - 4} fill={g.color ?? 'var(--text-2)'} className="so-chart__guide-label">
                {g.label} : {formatValue(g.value, valueFormat)}
              </text>
            </g>
          ))}
          {/* Repère de lecture : la verticale et les points survolés. */}
          {iHover !== null ? (
            <g aria-hidden="true" className="so-chart__cursor">
              <line x1={centreX} x2={centreX} y1={PAD_TOP} y2={baseline} />
              {traces.map((d) => (
                <circle key={d.key} cx={centreX}
                  cy={y(EMPILES.has(kind) && !d.overlay ? (couches.get(d.key)?.haut[iHover] ?? d.values[iHover]!) : d.values[iHover]!)} r={4.5}
                  fill={d.color} className="so-chart__cursor-dot" />
              ))}
            </g>
          ) : null}
        </svg>

        {/* MAINTENANT — au-dessus du tracé, sous le panneau de lecture.
            Positionné à la fraction de l'axe RÉEL, pas à la largeur du
            composant : il reste juste quel que soit le viewport. */}
        <NowMarker frac={nowFraction(model, nowMs)} variant="chart" />

        {iHover !== null && bucketT ? (
          <div className={`so-tip${aDroite ? ' so-tip--gauche' : ''}`}
            style={{ left: `${pct}%` }} role="presentation">
            <p className="so-tip__t">{titreDuBucket(new Date(bucketT).getTime(), bucketS, model.avecJour)}</p>
            <ul className="so-tip__list">
              {lignes.map((l) => (
                <li key={l.key}>
                  <span className="so-tip__dot" style={{ background: l.color }} aria-hidden="true" />
                  <span className="so-tip__label">{l.label}</span>
                  <span className="so-tip__val">{formatValue(l.value, valueFormat)}</span>
                </li>
              ))}
              {total !== null ? (
                <li className="so-tip__total">
                  <span className="so-tip__dot so-tip__dot--vide" aria-hidden="true" />
                  {/* Une série masquée : ce total n'est que celui de ce qui est affiché. */}
                  <span className="so-tip__label">{sansEcart ? 'Total affiché' : 'Total'}</span>
                  <span className="so-tip__val">{formatValue(total, valueFormat)}</span>
                </li>
              ) : null}
              {(guides ?? []).map((g, k) => (
                <li key={`g${k}`}>
                  <span className="so-tip__dot so-tip__dot--guide" style={{ color: g.color ?? 'var(--text-2)' }} aria-hidden="true" />
                  <span className="so-tip__label">{g.label}</span>
                  <span className="so-tip__val">{formatValue(g.value, valueFormat)}</span>
                </li>
              ))}
            </ul>
            {ecart ? (
              <p className={`so-tip__delta${ecart.abs >= 0 ? '' : ' so-tip__delta--bas'}`}>
                {ecart.abs >= 0 ? '+' : '−'}{formatValue(Math.abs(ecart.abs), valueFormat)}
                {ecart.pct === null ? '' : ` · ${ecart.abs >= 0 ? '+' : '−'}${Math.abs(Math.round(ecart.pct))} %`}
                {ecartLibelle ? <span className="so-tip__delta-de"> {ecartLibelle}</span> : null}
              </p>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="so-chart__labels" aria-hidden="true">
        {labels.map((l) => (
          <span key={`${l.x}-${l.text}`} style={{ left: `${(l.x / W) * 100}%` }}>{l.text}</span>
        ))}
      </div>
    </div>
  )
}
